import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowUpRight } from "lucide-react";
import { api } from "@/lib/api";

const STAGES = ["Idee", "Prototyp", "spielbarer Build", "veröffentlicht", "bereits LBE-erprobt"];
const YES_NO = [
  { v: "yes", l: "Ja" },
  { v: "no", l: "Nein" },
];

const INITIAL = {
  studio_name: "",
  contact_name: "",
  email: "",
  phone: "",
  website: "",
  game_name: "",
  development_stage: "",
  platform: "",
  multiplayer: "",
  lbe_experience: "",
  target_group: "",
  session_duration: "",
  short_description: "",
  trailer_url: "",
  build_link: "",
  message: "",
  consent: false,
};

export default function DevSubmissionForm() {
  const navigate = useNavigate();
  const [data, setData] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!data.consent) {
      toast.error("Bitte Datenschutzhinweis bestätigen");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/dev-submissions", data);
      navigate("/danke");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Anfrage fehlgeschlagen");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      className="surface p-6 md:p-10"
      data-testid="dev-submission-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <div>
          <label className="field-label">Studio / Entwicklername *</label>
          <input
            required
            className="field"
            value={data.studio_name}
            onChange={(e) => set("studio_name", e.target.value)}
            data-testid="input-studio-name"
          />
        </div>
        <div>
          <label className="field-label">Ansprechpartner *</label>
          <input
            required
            className="field"
            value={data.contact_name}
            onChange={(e) => set("contact_name", e.target.value)}
            data-testid="input-contact-name"
          />
        </div>
        <div>
          <label className="field-label">E-Mail *</label>
          <input
            required
            type="email"
            className="field"
            value={data.email}
            onChange={(e) => set("email", e.target.value)}
            data-testid="input-email"
          />
        </div>
        <div>
          <label className="field-label">Telefon (optional)</label>
          <input
            className="field"
            value={data.phone}
            onChange={(e) => set("phone", e.target.value)}
          />
        </div>
        <div>
          <label className="field-label">Website / Portfolio</label>
          <input
            className="field"
            value={data.website}
            onChange={(e) => set("website", e.target.value)}
          />
        </div>
        <div>
          <label className="field-label">Spielname *</label>
          <input
            required
            className="field"
            value={data.game_name}
            onChange={(e) => set("game_name", e.target.value)}
            data-testid="input-game-name"
          />
        </div>
        <div>
          <label className="field-label">Entwicklungsstand</label>
          <select
            className="field"
            value={data.development_stage}
            onChange={(e) => set("development_stage", e.target.value)}
            data-testid="select-stage"
          >
            <option value="">Bitte wählen</option>
            {STAGES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="field-label">Plattform / Engine</label>
          <input
            className="field"
            value={data.platform}
            onChange={(e) => set("platform", e.target.value)}
            placeholder="z. B. Unity, Unreal, Quest 3"
          />
        </div>
        <div>
          <label className="field-label">Multiplayer?</label>
          <select
            className="field"
            value={data.multiplayer}
            onChange={(e) => set("multiplayer", e.target.value)}
          >
            <option value="">Bitte wählen</option>
            {YES_NO.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label">LBE-Erfahrung?</label>
          <select
            className="field"
            value={data.lbe_experience}
            onChange={(e) => set("lbe_experience", e.target.value)}
          >
            <option value="">Bitte wählen</option>
            {YES_NO.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
          </select>
        </div>
        <div>
          <label className="field-label">Zielgruppe</label>
          <input
            className="field"
            value={data.target_group}
            onChange={(e) => set("target_group", e.target.value)}
            placeholder="z. B. Gruppen, Familien, Teenager"
          />
        </div>
        <div>
          <label className="field-label">Geplante Sessiondauer</label>
          <input
            className="field"
            value={data.session_duration}
            onChange={(e) => set("session_duration", e.target.value)}
            placeholder="z. B. 15 min"
          />
        </div>
        <div className="md:col-span-2">
          <label className="field-label">Kurzbeschreibung</label>
          <textarea
            className="field"
            rows={3}
            value={data.short_description}
            onChange={(e) => set("short_description", e.target.value)}
          />
        </div>
        <div>
          <label className="field-label">Trailer / Video-Link</label>
          <input
            className="field"
            value={data.trailer_url}
            onChange={(e) => set("trailer_url", e.target.value)}
          />
        </div>
        <div>
          <label className="field-label">Build-Link (optional)</label>
          <input
            className="field"
            value={data.build_link}
            onChange={(e) => set("build_link", e.target.value)}
          />
        </div>
        <div className="md:col-span-2">
          <label className="field-label">Nachricht</label>
          <textarea
            className="field"
            rows={3}
            value={data.message}
            onChange={(e) => set("message", e.target.value)}
          />
        </div>
      </div>

      <div className="hairline my-8" />

      <label className="flex items-start gap-3 cursor-pointer">
        <input
          type="checkbox"
          checked={data.consent}
          onChange={(e) => set("consent", e.target.checked)}
          className="mt-1 accent-[#18E096] w-4 h-4"
          data-testid="input-consent"
        />
        <span className="text-sm text-[#AEB7B7] leading-relaxed">
          Ich bin damit einverstanden, dass meine Angaben zur Bearbeitung meiner Einreichung
          verarbeitet werden. Eine Einreichung ist noch keine Zusage für Test, Aufnahme oder
          Vergütung.
        </span>
      </label>

      <div className="mt-8">
        <button
          type="submit"
          disabled={submitting}
          className="btn-primary disabled:opacity-50"
          data-testid="dev-submit"
        >
          {submitting ? (
            <>
              <Loader2 size={16} strokeWidth={1.5} className="animate-spin" /> Wird gesendet…
            </>
          ) : (
            <>
              Konzept einreichen <ArrowUpRight size={16} strokeWidth={1.5} />
            </>
          )}
        </button>
      </div>
    </form>
  );
}
